import { ImageCaptionDTO, RawImageCaption } from './api.types';

export interface IServiceTranslationContent {
  name?: string;
  description?: string;
  key_box_pickup_instructions?: string;
  key_box_return_instructions?: string;
}

export interface IServiceTranslation {
  [key: string]: IServiceTranslationContent;
}

export interface LinkedHarbor {
  id: string;
  name: string;
  description: string;
  location: string;
  contact_phone: string;
  contact_email: string;
  latitude: string;
  longitude: string;
  berth_capacity: string;
  max_depth: string;
  min_depth: string;
  depth_unit: string;
  website_url?: string;
  [key: string]: string | number | boolean | null | undefined;
}

export interface IServiceData {
  id?: string;
  name: string;
  description: string;
  category: string;
  sub_category: string;
  service_type: string;
  language: string;
  translations?: IServiceTranslation;
  source_language: string;
  media?: File[] | string[];
  linked_harbor_ids?: string[];
  harbor_id?: string;
  linked_harbors?: LinkedHarbor[];
  latitude?: number;
  longitude?: number;
  distance?: number;
  distance_unit?: 'm' | 'km';
  public_visibility?: boolean;
  images?: string[];
  service_provider?: Record<string, unknown>;
  service_provider_id?: string;
  created_at?: string;
  updated_at?: string;
  independent_booking_enabled?: boolean;
  booking_slot_type?: BookingSlotType;
  start_time?: string;
  end_time?: string;
  send_request_booking_to_admin?: boolean;
  booking_policy_text?: string;
  price_amount?: number;
  price_currency?: string;
  quantity?: number;
  capacity_status?: CapacityStatus;
  session_slots?: { start_time: string; end_time: string }[];
  media_captions?: ImageCaptionDTO[];
  image_captions?: RawImageCaption[];
  moderation_state?: ModerationState;
  rejection_reason?: string | null;
  transaction_presentation_mode?: 'RESERVATION' | 'PAYMENT_ONLY' | null;
  access_handover_mode?: 'NONE' | 'KEY_BOX' | 'ACCESS_CODE';
  key_box_code?: string;
  key_box_pickup_instructions?: string;
  key_box_return_instructions?: string;
  harbour_service_model?: HarbourServiceModel;
  contact_email?: string;
  contact_phone?: string;
  country_code?: string;
  country_short_code?: string;
  contact_phone_country_short_code?: string;
}

export interface ServiceResponse {
  status: boolean;
  message: string;
  data: IServiceData;
}

export interface ServicesListResponse {
  status: boolean;
  message: string;
  data: IServiceData[];
  totalItems?: number;
  totalPages?: number;
  currentPage?: number;
  total?: number;
  page?: number;
  limit?: number;
}

export interface ServiceListQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  category?: string;
  sub_category?: string;
  service_type?: string;
  language?: string;
  service_provider_id?: string;
}

export interface CreateServicePayload {
  name: string;
  description: string;
  category: string;
  sub_category: string;
  service_type: string;
  language: string;
  translations?: IServiceTranslation;
  source_language: string;
  media?: File[] | string[];
}

export interface UpdateServicePayload {
  name?: string;
  description?: string;
  category?: string;
  sub_category?: string;
  service_type?: string;
  language?: string;
  translations?: IServiceTranslation;
  source_language?: string;
  media?: File[] | string[];
}

export enum ServiceType {
  ONSITE = 'onsite',
  OFFSITE = 'offsite',
}

export enum HarbourServiceModel {
  LISTING = 'listing',
  BOOKABLE = 'bookable',
  REQUEST = 'request',
}

export enum BookingSlotType {
  HOURLY = 'hourly',
  DAILY = 'daily',
  SESSION = 'session',
}

export enum CapacityStatus {
  AVAILABLE = 'available',
  LIMITED = 'limited',
  NOT_AVAILABLE = 'not_available',
}

export enum ModerationState {
  DRAFT = 'DRAFT',
  SUBMITTED = 'SUBMITTED',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
  DISABLED = 'DISABLED',
}

export enum ServiceCategory {
  ESSENTIALS = 'essentials',
  FOOD_AND_DRINK = 'food_and_drink',
  SANITATION = 'sanitation',
  TRANSPORT = 'transport',
  LOGISTICS = 'logistics',
  TECHNICAL = 'technical',
  MEDICAL = 'medical',
  FINANCE = 'finance',
  MOBILITY = 'mobility',
  LOCAL = 'local',
}

export enum ServiceLanguage {
  EN = 'en',
  FI = 'fi',
  SV = 'sv',
  DE = 'de',
}

export interface ServiceIcon {
  id: number;
  name: string;
  imageUrl: string;
}

export interface ServiceTranslations {
  [key: string]: {
    name: string;
    description: string;
  };
}

export interface SubCategoryItem {
  id: string;
  name: string;
  category: string;
  category_label?: string;
  translations: ServiceTranslations;
  images: string[] | null;
  icon: ServiceIcon;
  created_at: string;
  updated_at: string;
  value?: string;
  label?: string;
}

export interface SubCategoryResponse {
  status: boolean;
  message: string;
  data: SubCategoryItem[];
  total: number;
  page: number;
  limit: number;
}
